import { getRandomInt } from './get-random-int';

/**
 * List of verification code PDF filenames in public/verification_codes/.
 * Each filename is file_<md5(4-digit-code)>.pdf.
 */
export const VERIFICATION_CODE_PDF_FILENAMES: string[] = [
  'file_f2b5e92f61b6de923b063588ee6e7c48.pdf',
  'file_e3c9cb08585a40829bc88130b0b8ebdf.pdf',
  'file_dd111a74f4ff9cf2cbf198cef2579800.pdf',
  'file_d9d3837ee7981e8c064774da6cdd98bf.pdf',
  'file_d4ea5dacfff2d8a35c0952291779290d.pdf',
  'file_d309cd6396e744600c943fada20f686a.pdf',
  'file_c8cc6e90ccbff44c9cee23611711cdc4.pdf',
  'file_c5866e93cab1776890fe343c9e7063fb.pdf',
  'file_c165ed49edbd0e69b120bee701bdb794.pdf',
  'file_be7485be5b6eb3690efcbc9e95e8f15a.pdf',
  'file_be3ac64e67e84198f03f45b661f2124a.pdf',
  'file_ba9a56ce0a9bfa26e8ed9e10b2cc8f46.pdf',
  'file_abdbeb4d8dbe30df8430a8394b7218ef.pdf',
  'file_a3a3e8b30dd6eadfc78c77bb2b8e6b60.pdf',
  'file_a1bcb47486d5abaeabf8fc1d64abe62b.pdf',
  'file_a0b83c02d720415dada82e08bc09e9f3.pdf',
  'file_9ed27554c893b5bad850a422c3538c15.pdf',
  'file_9824f9c1543628a85bb51d2dd6fcf8a3.pdf',
  'file_96b250a90d3cf0868c83f8c965142d2a.pdf',
  'file_8e68c3c7bf14ad0bcaba52babfa470bd.pdf',
  'file_8dc5983b8c4ef1d8fcd5f325f9a65511.pdf',
  'file_8c6d39a0e9e6f344fb6a2d83c7c6787c.pdf',
  'file_80c94c09453dfe07681fde78e769353f.pdf',
  'file_7eb532aef980c36170c0b4426f082b87.pdf',
  'file_7cce53cf90577442771720a370c3c723.pdf',
  'file_7beadac50e1ce55dc31343cc1a89ac5c.pdf',
  'file_7a951116de2a4c23c74733d76046a5b4.pdf',
  'file_7a68443f5c80d181c42967cd71612af1.pdf',
  'file_78bc62d08a9a0b9b0b9c0ad339ef82d3.pdf',
  'file_73bf6c41e241e28b89d0fb9e0c82f9ce.pdf',
  'file_6ba9580871babd8d9c620aa5fb366abf.pdf',
  'file_6872937617af85db5a39a5243e858d1f.pdf',
  'file_51c68dc084cb0b8467eafad1330bce66.pdf',
  'file_51beafc370abd4f00aa270ee3b626849.pdf',
  'file_464d828b85b0bed98e80ade0a5c43b0f.pdf',
  'file_3d324c2883882b15fa8fbe8f025a3a99.pdf',
  'file_36107e6b72b64a32dfce109719e892ad.pdf',
  'file_272e11700558e27be60f7489d2d782e7.pdf',
  'file_25db67c5657914454081c6a18e93d6dd.pdf',
  'file_21ca6d0cf2f25c4dbb35d8dc0b679c3f.pdf',
  'file_182bd81ea25270b7d1c2fe8353d17fe6.pdf',
  'file_1558417b096b5d8e7cbe0183ea9cbf26.pdf',
  'file_144a3f71a03ab7c4f46f9656608efdb2.pdf',
  'file_0d4f4805c36dc6853edfa4c7e1638b48.pdf',
  'file_09d90af096ee93aae1cecbb338d43020.pdf',
];

const VERIFICATION_CODES_BASE = 'verification_codes/';

/**
 * Returns the base URL for verification code assets (with trailing slash).
 */
function getVerificationCodesBaseUrl(): string {
  const baseUrl = import.meta.env.BASE_URL;
  const pathSep = baseUrl.endsWith('/') ? '' : '/';
  return `${baseUrl}${pathSep}${VERIFICATION_CODES_BASE}`;
}

/**
 * Fetches a specific verification code PDF by filename from the static assets.
 * @param filename - follows pattern "file_<md5>.pdf"
 * @returns Promise resolving to { blob, filename } or null if fetch fails
 */
export async function fetchVerificationPdf(filename: string): Promise<{
  blob: Blob;
  filename: string;
} | null> {
  const url = getVerificationCodesBaseUrl() + filename;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return { blob, filename };
  } catch {
    return null;
  }
}

/**
 * Fetches a random verification code PDF from the static assets.
 * @returns Promise resolving to { blob, filename } or null if fetch fails
 */
export async function fetchRandomVerificationPdf(): Promise<{
  blob: Blob;
  filename: string;
} | null> {
  const index = getRandomInt(0, VERIFICATION_CODE_PDF_FILENAMES.length - 1);
  const filename = VERIFICATION_CODE_PDF_FILENAMES[index];
  const url = getVerificationCodesBaseUrl() + filename;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return { blob, filename };
  } catch {
    return null;
  }
}

const FILE_PREFIX = 'file_';
const FILE_SUFFIX = '.pdf';
const MD5_SHIFT_AMOUNTS = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5,
  9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11,
  16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15,
  21,
];
const MD5_TABLE = Array.from(
  { length: 64 },
  (_, index) => Math.floor(Math.abs(Math.sin(index + 1)) * 2 ** 32) >>> 0,
);

function leftRotate(value: number, amount: number): number {
  return ((value << amount) | (value >>> (32 - amount))) >>> 0;
}

function toLittleEndianHex(value: number): string {
  const bytes = [
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff,
  ];

  return bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function md5Hex(value: string): string {
  const bytes = Array.from(new TextEncoder().encode(value));
  const bitLength = bytes.length * 8;

  bytes.push(0x80);
  while (bytes.length % 64 !== 56) {
    bytes.push(0);
  }

  for (let index = 0; index < 8; index += 1) {
    bytes.push(Math.floor(bitLength / 2 ** (8 * index)) & 0xff);
  }

  let a = 0x67452301;
  let b = 0xefcdab89;
  let c = 0x98badcfe;
  let d = 0x10325476;

  for (let chunkStart = 0; chunkStart < bytes.length; chunkStart += 64) {
    const words = Array.from({ length: 16 }, (_, index) => {
      const offset = chunkStart + index * 4;
      return (
        (bytes[offset] |
          (bytes[offset + 1] << 8) |
          (bytes[offset + 2] << 16) |
          (bytes[offset + 3] << 24)) >>>
        0
      );
    });

    let chunkA = a;
    let chunkB = b;
    let chunkC = c;
    let chunkD = d;

    for (let index = 0; index < 64; index += 1) {
      let f: number;
      let wordIndex: number;

      if (index < 16) {
        f = (chunkB & chunkC) | (~chunkB & chunkD);
        wordIndex = index;
      } else if (index < 32) {
        f = (chunkD & chunkB) | (~chunkD & chunkC);
        wordIndex = (5 * index + 1) % 16;
      } else if (index < 48) {
        f = chunkB ^ chunkC ^ chunkD;
        wordIndex = (3 * index + 5) % 16;
      } else {
        f = chunkC ^ (chunkB | ~chunkD);
        wordIndex = (7 * index) % 16;
      }

      const nextD = chunkC;
      chunkC = chunkB;
      chunkB =
        (chunkB +
          leftRotate(
            (chunkA + f + MD5_TABLE[index] + words[wordIndex]) >>> 0,
            MD5_SHIFT_AMOUNTS[index],
          )) >>>
        0;
      chunkA = chunkD;
      chunkD = nextD;
    }

    a = (a + chunkA) >>> 0;
    b = (b + chunkB) >>> 0;
    c = (c + chunkC) >>> 0;
    d = (d + chunkD) >>> 0;
  }

  return [a, b, c, d].map(toLittleEndianHex).join('');
}

/**
 * Extracts the hash from a verification PDF filename (file_<hash>.pdf).
 * @returns The hash as-is, or null if the filename does not match the expected pattern
 */
export function extractHashFromPdfFilename(pdfFilename: string): string | null {
  if (
    !pdfFilename.startsWith(FILE_PREFIX) ||
    !pdfFilename.endsWith(FILE_SUFFIX)
  ) {
    return null;
  }
  const hash = pdfFilename.slice(
    FILE_PREFIX.length,
    pdfFilename.length - FILE_SUFFIX.length,
  );
  return /^[a-f0-9]{32}$/.test(hash) ? hash : null;
}

/**
 * Verifies that the MD5 hash of the entered code matches the hash encoded in the PDF filename.
 * @param enteredCode - The 4-digit (or any) code entered by the user
 * @param pdfFilename - Filename of the PDF, e.g. file_<md5>.pdf
 * @returns true if the MD5 of enteredCode equals the hash in the filename (exact match)
 */
export function verifyPdfCode(
  enteredCode: string,
  pdfFilename: string,
): boolean {
  const hashFromFilename = extractHashFromPdfFilename(pdfFilename);
  if (hashFromFilename === null) {
    return false;
  }
  // md5() returns lowercase hex; filenames use the same format
  const computedHash = md5Hex(enteredCode);
  return computedHash === hashFromFilename;
}
